/**
 * Signing a browser out after a stretch of doing nothing.
 *
 * The Worker is what actually enforces this — a session row carries the instant
 * it was last used, and one that has sat still past the window is deleted on the
 * next request (`IDLE_MINUTES` in `users.ts`, reported to the app as
 * `config.idleMinutes` so the two halves cannot drift apart). This is the other
 * half of it, and it exists because a rule nobody can see is a rule nobody
 * trusts: without it a tab left open all afternoon goes on showing a dashboard,
 * a licence key and an invoice history until somebody clicks something, and only
 * then admits it stopped being signed in an hour ago.
 *
 * Two jobs, and they pull in opposite directions on purpose:
 *
 *   the timer      nothing from the person for the whole window → sign out
 *   the heartbeat  something from the person, and the session getting old →
 *                  one cheap request, so that *using* the app keeps it alive
 *
 * Without the heartbeat, reading a long page for an hour would be counted as an
 * hour of inactivity by the Worker, which measures requests and not attention.
 */
import { useEffect, useRef } from 'react';

/**
 * What counts as the person being here. Deliberately coarse — the question is
 * "is somebody at this machine", not "what did they do" — and all passive, so
 * none of them can delay a scroll or a keystroke.
 */
const ACTIVITY = ['pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'] as const;

/**
 * How long the app will let the stored instant go stale before spending a
 * request to push it forward. Well inside any sane window, and far enough apart
 * that a person typing continuously costs one request every few minutes rather
 * than one per keystroke.
 */
const HEARTBEAT_MS = 5 * 60_000;

export function useIdleSignOut({
  active,
  minutes,
  onHeartbeat,
  onIdle
}: {
  /** Off entirely while nobody is signed in. */
  active: boolean;
  /** The Worker's window, so there is one number rather than two. */
  minutes: number;
  /** Push the session forward. Any authenticated request does; `/me` is the cheapest. */
  onHeartbeat: () => void;
  onIdle: () => void;
}): void {
  // Held in refs rather than in state: every one of these changes on activity,
  // and re-rendering the whole shell because somebody moved would be a cost
  // paid for nothing.
  const idleAt = useRef(0);
  const beatAt = useRef(0);

  useEffect(() => {
    if (!active || minutes <= 0) return;

    const window_ms = minutes * 60_000;
    let timer = 0;

    const arm = (): void => {
      window.clearTimeout(timer);
      idleAt.current = Date.now() + window_ms;
      timer = window.setTimeout(onIdle, window_ms);
    };

    const seen = (): void => {
      const now = Date.now();
      // Past the window already: the timer is about to fire, or the tab was
      // asleep through it and is only now being woken. Either way this is not
      // activity that should push the window forward.
      if (idleAt.current && now > idleAt.current) return;
      if (now - beatAt.current > HEARTBEAT_MS) {
        beatAt.current = now;
        onHeartbeat();
      }
      arm();
    };

    // A sleeping tab does not run its timeouts on time, so the window is
    // re-checked against the clock when the tab comes back rather than trusted
    // to have fired while the machine was shut.
    const woke = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (idleAt.current && Date.now() > idleAt.current) onIdle();
    };

    beatAt.current = Date.now();
    arm();
    for (const event of ACTIVITY) window.addEventListener(event, seen, { passive: true });
    document.addEventListener('visibilitychange', woke);

    return () => {
      window.clearTimeout(timer);
      for (const event of ACTIVITY) window.removeEventListener(event, seen);
      document.removeEventListener('visibilitychange', woke);
    };
  }, [active, minutes, onHeartbeat, onIdle]);
}
